import React from 'react';
import { Box, Typography, Paper, Container, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MainLayout from '../../components/layout/MainLayout';

const FaqPage: React.FC = () => {
  const faqs = [
    {
      question: "What is the MathWiz League?",
      answer: "The MathWiz League is a summer math competition and enrichment program for middle school students. It's designed to keep students engaged with fun, challenging math problems throughout the summer."
    },
    {
      question: "Who runs the league?",
      answer: "The league is run by a middle school teacher and a team of former students who are passionate about math and mentoring."
    },
    {
      question: "Who can participate?",
      answer: "Any student entering grades 6–8 is welcome to join, no matter what school they attend. All you need is curiosity and a willingness to solve problems."
    },
    {
      question: "How does it work?",
      answer: "Each week, we post a set of math problems on our website. Students solve and submit their answers online. Points are awarded for correct answers, and the leaderboard is updated weekly."
    },
    {
      question: "What kind of problems are included?",
      answer: "We include a variety of problems — from logic and puzzles to geometry, number theory, and algebra. The problems are designed to be challenging but approachable, even without advanced coursework."
    },
    {
      question: "How much does it cost?",
      answer: "There's a one-time registration fee to join the league. We keep the price affordable so that more students can participate. Most families find the experience well worth the cost."
    },
    {
      question: "What's included in the registration fee?",
      answer: "Your registration covers weekly problem sets, detailed solution write-ups, a personalized student dashboard, virtual awards, achievement tracking, and access to the leaderboard and trophy case features."
    },
    {
      question: "What are the virtual awards and trophy case?",
      answer: "As students participate, they earn virtual achievements — for things like perfect submissions, weekly top scores, creative solutions, and more. Each student has a digital trophy case where these awards are displayed. Students can also view other participants' trophy cases via the leaderboard."
    },
    {
      question: "How do students submit their answers?",
      answer: "Students submit their answers directly through our website each week. It's simple and mobile-friendly."
    },
    {
      question: "Do students get feedback or solutions?",
      answer: "Yes! After each week's deadline, we post full, detailed solutions to help students understand the reasoning behind each answer."
    },
    {
      question: "Are there prizes?",
      answer: "We focus on virtual achievements, recognition, and skill-building. However, there may be special shoutouts and digital certificates for top performers or standout improvements."
    },
    {
      question: "Can parents or teachers track student progress?",
      answer: "Yes — parents and teachers can opt into weekly progress reports to follow along with their student's journey."
    },
    {
      question: "What if a student misses a week?",
      answer: "No problem! While scores are cumulative, students can miss a week and still participate in the rest of the league. It's flexible and low-pressure."
    },
    {
      question: "How do I sign up?",
      answer: "Just head to our website and click \"Register.\" Once you complete the sign-up form and pay the fee, your student will be ready to dive in."
    }
  ];

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" color="primary" fontWeight="bold">
            Frequently Asked Questions
          </Typography>
          
          <Divider sx={{ mb: 4, mt: 2 }} />
          
          <Box sx={{ my: 2 }}>
            {faqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{ 
                    backgroundColor: 'rgba(63, 81, 181, 0.05)',
                    borderRadius: '4px',
                    '&.Mui-expanded': {
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0
                    }
                  }}
                >
                  <Typography fontWeight="500" color="primary.dark">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2, pb: 3 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default FaqPage;